import { z } from 'zod';

/**
 * @file approximate-date.schema.ts
 * @description Data que a pessoa pode não lembrar com precisão.
 *
 * Nasceu como `LmpDateSchema`, para a DUM, mas a forma serve a qualquer
 * pergunta cuja resposta honesta é "mais ou menos": desde quando você quer
 * engravidar, desde quando parou o método contraceptivo, quando foi a perda.
 *
 * Extraído para cá quando a Fase 5 precisou dela — reusar um schema chamado
 * `Lmp` para "desde quando tenta" seria criar confusão de nome, e definir uma
 * segunda igual seria criar mais uma duplicata num repositório que já teve
 * cinco vocabulários para a mesma coisa.
 */
export const ApproximatePeriodEnum = z.enum(['beginning', 'middle', 'end']);

export const ApproximateDateSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('exact'), date: z.coerce.date() }),
  z.object({
    type: z.literal('approximate'),
    year: z.number().int(),
    month: z.number().int(),
    period: ApproximatePeriodEnum,
  }),
  z.object({ type: z.literal('unknown') }),
]);

export type ApproximateDate = z.infer<typeof ApproximateDateSchema>;

/**
 * Converte para uma data utilizável em cálculo. O período aproximado vira dia
 * 5, 15 ou 25 — precisão suficiente para "há quanto tempo", que é sempre
 * exibido em meses ou anos.
 */
export function approximateDateToDate(d: ApproximateDate | null | undefined): Date | null {
  if (!d) return null;
  if (d.type === 'unknown') return null;
  if (d.type === 'exact') {
    const dt = d.date instanceof Date ? d.date : new Date(d.date);
    return isNaN(dt.getTime()) ? null : dt;
  }
  const dia = d.period === 'beginning' ? 5 : d.period === 'middle' ? 15 : 25;
  return new Date(Date.UTC(d.year, d.month - 1, dia));
}

/** Meses inteiros entre a data e agora. `null` quando não dá para calcular. */
export function mesesDesde(
  d: ApproximateDate | null | undefined,
  agora: Date = new Date(),
): number | null {
  const inicio = approximateDateToDate(d);
  if (!inicio) return null;
  const meses =
    (agora.getUTCFullYear() - inicio.getUTCFullYear()) * 12 +
    (agora.getUTCMonth() - inicio.getUTCMonth());
  return Math.max(0, agora.getUTCDate() < inicio.getUTCDate() ? meses - 1 : meses);
}

/** "3 anos", "8 meses", "1 ano e 2 meses". */
export function formatarDuracao(meses: number | null): string | null {
  if (meses === null) return null;
  if (meses === 0) return 'menos de 1 mês';
  if (meses < 12) return `${meses} ${meses === 1 ? 'mês' : 'meses'}`;
  const anos = Math.floor(meses / 12);
  const resto = meses % 12;
  const parteAnos = `${anos} ${anos === 1 ? 'ano' : 'anos'}`;
  if (resto === 0) return parteAnos;
  return `${parteAnos} e ${resto} ${resto === 1 ? 'mês' : 'meses'}`;
}
